import { RingAirProductIdMap } from 'content/products/ring/ringAirProductIds';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { isCartApiSuccess, makeStoreApiRequest } from './utils';

/** Hidden on checkout UI and customer emails. Admin still shows it. Packing slip is Liquid, not a platform hide. */
export const RING_AIR_GIFT_PROPERTY_KEY = '_gift';
export const RING_AIR_GIFT_PROPERTY_VALUE = 'true';

/** Checkout reconcile must not hang the redirect. Toggle/add get the same cap. */
const GIFT_STAMP_TIMEOUT_MS = 4000;

export type CartLineAttribute = { key: string; value: string };

export type GiftStampLineUpdate = {
  id: string;
  attributes: CartLineAttribute[];
};

type CartLineNode = {
  id: string;
  quantity: number;
  merchandise?: {
    id?: string;
    product?: { id?: string };
  };
  attributes?: CartLineAttribute[];
};

function attributesMatch(
  current: CartLineAttribute[] | undefined,
  next: CartLineAttribute[],
): boolean {
  const currentList = current ?? [];
  if (currentList.length !== next.length) return false;
  const currentMap = new Map(currentList.map((attr) => [attr.key, attr.value]));
  return next.every((attr) => currentMap.get(attr.key) === attr.value);
}

export function mergeRingAirGiftAttribute(
  attributes: CartLineAttribute[] | undefined,
  giftOn: boolean,
): CartLineAttribute[] {
  const next = (attributes ?? []).filter(
    (attr) => attr.key !== RING_AIR_GIFT_PROPERTY_KEY,
  );
  if (giftOn) {
    next.push({
      key: RING_AIR_GIFT_PROPERTY_KEY,
      value: RING_AIR_GIFT_PROPERTY_VALUE,
    });
  }
  return next;
}

/**
 * Ring AIR lines whose `_gift` property does not yet match gift flow.
 * Empty when there is nothing to write (skip the extra Shopify round-trip).
 *
 * Attributes-only update: `cartLinesUpdate` treats quantity and merchandiseId
 * as overwrites. Echoing them from a stale React cart can clamp qty.
 */
export function getRingAirGiftStampUpdates(
  lines: { edges?: Array<{ node: CartLineNode }> } | undefined,
  store: ShopifyStore,
  giftOn: boolean,
): GiftStampLineUpdate[] {
  const allow = new Set(RingAirProductIdMap[store] ?? []);
  const updates: GiftStampLineUpdate[] = [];

  for (const edge of lines?.edges ?? []) {
    const node = edge.node;
    const productId = node.merchandise?.product?.id;
    if (!node.id || !productId || !allow.has(productId)) continue;

    const attributes = mergeRingAirGiftAttribute(node.attributes, giftOn);
    if (attributesMatch(node.attributes, attributes)) continue;

    updates.push({
      id: node.id,
      attributes,
    });
  }

  return updates;
}

export function cartHasGiftFlow(
  cart: { attributes?: CartLineAttribute[] } | null | undefined,
): boolean {
  return (
    cart?.attributes?.some(
      (attr) => attr.key === 'gift_flow' && attr.value === 'true',
    ) === true
  );
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('gift stamp timeout')), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Write or strip `_gift` on Ring AIR lines. Returns the input cart unchanged
 * when there is nothing to do, or when the update fails / times out (fail-open).
 */
export async function applyRingAirGiftStamp<T extends Record<string, any>>({
  cart,
  store,
  giftOn,
}: {
  cart: T;
  store: ShopifyStore;
  giftOn: boolean;
}): Promise<T> {
  if (!cart?.id) return cart;
  const lines = getRingAirGiftStampUpdates(cart.lines, store, giftOn);
  if (lines.length === 0) return cart;

  try {
    const response = await withTimeout(
      makeStoreApiRequest(store, `/api/vendor/shopify/cart/update/`, {
        cartId: cart.id,
        lines,
      }),
      GIFT_STAMP_TIMEOUT_MS,
    );
    const userErrors = Array.isArray(
      (response as { userErrors?: unknown }).userErrors,
    )
      ? (
          response as {
            userErrors: Array<{ field?: string[]; message?: string }>;
          }
        ).userErrors
      : [];
    if (userErrors.length > 0) {
      console.error('[Cart] Ring AIR _gift stamp userErrors', userErrors);
    }
    if (!isCartApiSuccess(response) || !response.cart?.id) {
      console.error('[Cart] Ring AIR _gift stamp failed', { giftOn, response });
      return cart;
    }
    return response.cart as T;
  } catch (error) {
    console.error('[Cart] Ring AIR _gift stamp failed', { giftOn, error });
    return cart;
  }
}
